import { useCallback, useSyncExternalStore } from 'react';

/**
 * The palette a person chose. `system` follows the operating system — the only behaviour the SPA
 * had before — and stamps nothing on the root, so `tokens.css`'s `prefers-color-scheme` block
 * decides; the other two stamp `data-theme`, which `tokens.css` lets win in both directions.
 */
export type ThemePreference = 'system' | 'light' | 'dark';

export const THEME_PREFERENCES: readonly ThemePreference[] = ['system', 'light', 'dark'];

export const THEME_LABELS: Readonly<Record<ThemePreference, string>> = {
  system: 'Sistema',
  light: 'Claro',
  dark: 'Escuro',
};

const STORAGE_KEY = 'instoque.tema';

function isPreference(value: unknown): value is ThemePreference {
  return value === 'system' || value === 'light' || value === 'dark';
}

/** A per-device convenience: storage that is blocked or cleared simply means `system`. */
export function readThemePreference(): ThemePreference {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return isPreference(stored) ? stored : 'system';
  } catch {
    return 'system';
  }
}

export function applyThemePreference(preference: ThemePreference): void {
  const root = document.documentElement;
  if (preference === 'system') {
    root.removeAttribute('data-theme');
  } else {
    root.setAttribute('data-theme', preference);
  }
}

function saveThemePreference(preference: ThemePreference): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, preference);
  } catch {
    // Not persisted; the choice still holds for this visit.
  }
}

// One preference for the whole page: the top bar's menu, the sidebar's menu and the ⌘K palette
// each read it, and a choice made in one must show in the others at once.
let current: ThemePreference | null = null;
const listeners = new Set<() => void>();

function snapshot(): ThemePreference {
  current ??= readThemePreference();
  return current;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function publish(next: ThemePreference): void {
  current = next;
  for (const listener of listeners) {
    listener();
  }
}

/**
 * The current preference and a setter that applies, persists and — where the browser can —
 * cross-fades the whole page into the new palette instead of snapping every surface at once.
 */
export function useThemePreference(): readonly [ThemePreference, (next: ThemePreference) => void] {
  const preference = useSyncExternalStore(subscribe, snapshot, () => 'system' as const);

  const choose = useCallback((next: ThemePreference) => {
    const update = () => {
      applyThemePreference(next);
      saveThemePreference(next);
      publish(next);
    };
    const root = document.documentElement;
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    if (reduced || typeof document.startViewTransition !== 'function') {
      update();
      return;
    }
    root.classList.add('theme-switch');
    const transition = document.startViewTransition(update);
    void transition.finished.finally(() => {
      root.classList.remove('theme-switch');
    });
  }, []);

  return [preference, choose] as const;
}

/** Test seam: forget the cached preference so the next read goes back to storage. */
export function resetThemePreferenceForTests(): void {
  current = null;
}
